<template>
  <div class="reports-page space-y-5">
    <!-- Header -->
    <div>
      <h1 class="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">Reports</h1>
      <p class="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">Generate and send UCO collection reports</p>
    </div>

    <!-- Alert for No Data -->
    <div
      v-if="noDataAlert"
      class="flex items-center gap-3 px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 rounded-xl text-sm text-amber-700 dark:text-amber-300"
    >
      <span class="material-icons text-lg" aria-hidden="true">warning</span>
      No records found for the selected parameters.
      <button @click="noDataAlert = false" aria-label="Dismiss alert" class="ml-auto text-amber-400 hover:text-amber-600 dark:hover:text-amber-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 rounded">
        <span class="material-icons text-lg" aria-hidden="true">close</span>
      </button>
    </div>

    <!-- Report Type Selection -->
    <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 p-6">
      <label for="rpt-type" class="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">
        Select Report Type
      </label>
      <select
        id="rpt-type"
        v-model="selectedReportType"
        class="w-full px-4 py-2.5 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 transition-all duration-200"
      >
        <option value="supplier">Select Supplier Report</option>
        <option value="supplier_location">
          Select Supplier and Locations Report
        </option>
      </select>
    </div>

    <!-- Supplier Report -->
    <div v-if="selectedReportType === 'supplier'" class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 p-6 space-y-4">
      <!-- Supplier Selection -->
      <div>
        <label for="rpt-suppliers" class="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">
          Select Suppliers
        </label>
        <v-select
          input-id="rpt-suppliers"
          :options="suppliers"
          v-model="selectedSupplierIds"
          label="suppliername"
          multiple
          :reduce="(supplier) => supplier.supplierid"
          class="v-select-custom"
        ></v-select>
      </div>
      <!-- Display Selected Suppliers -->
      <div v-if="selectedSupplierIds.length" class="space-y-2">
        <p class="text-sm font-semibold text-neutral-700 dark:text-neutral-300">Selected Suppliers:</p>
        <div class="flex flex-wrap gap-2">
          <div
            v-for="supplierId in selectedSupplierIds"
            :key="supplierId"
            class="flex items-center gap-2 bg-primary-50 dark:bg-primary-900/30 px-3 py-2 rounded-lg"
          >
            <span class="text-sm text-neutral-700 dark:text-neutral-300">{{ getSupplierName(supplierId) }}</span>
            <button
              @click="removeSelectedSupplier(supplierId)"
              class="w-6 h-6 flex items-center justify-center rounded-full bg-primary-200 dark:bg-primary-700 hover:bg-primary-300 dark:hover:bg-primary-600 text-primary-700 dark:text-primary-200 transition-colors"
              aria-label="Remove supplier"
            >
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Supplier and Locations Report -->
    <div v-else-if="selectedReportType === 'supplier_location'" class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 p-6 space-y-4">
      <!-- Supplier Selection -->
      <div>
        <label for="rpt-suppliers-loc" class="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">
          Select Suppliers
        </label>
        <v-select
          input-id="rpt-suppliers-loc"
          :options="suppliersWithLocations"
          v-model="selectedSupplierWithLocationsIds"
          label="suppliername"
          multiple
          :reduce="(supplier) => supplier.supplierid"
          class="v-select-custom"
        ></v-select>
      </div>
      <!-- Locations Selection -->
      <div v-if="selectedSupplierWithLocationsIds.length" class="space-y-4">
        <div
          v-for="supplierId in selectedSupplierWithLocationsIds"
          :key="supplierId"
          class="bg-neutral-50 dark:bg-neutral-700 p-4 rounded-lg"
        >
          <label :for="`rpt-loc-${supplierId}`" class="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">
            Select Locations for {{ getSupplierName(supplierId) }}
          </label>
          <v-select
            :input-id="`rpt-loc-${supplierId}`"
            :options="getLocationsForSupplier(supplierId)"
            v-model="selectedLocationsPerSupplier[supplierId]"
            label="locationname"
            multiple
            :reduce="(location) => location.locationid"
            class="v-select-custom"
          ></v-select>
        </div>
        <!-- Display Selected Locations -->
        <div class="space-y-3">
          <p class="text-sm font-semibold text-neutral-700 dark:text-neutral-300">Selected Locations:</p>
          <div
            v-for="supplierId in selectedSupplierWithLocationsIds"
            :key="supplierId"
          >
            <div
              v-if="
                selectedLocationsPerSupplier[supplierId] &&
                selectedLocationsPerSupplier[supplierId].length
              "
              class="space-y-2"
            >
              <p class="text-sm font-semibold text-secondary-700 dark:text-secondary-400">{{ getSupplierName(supplierId) }}:</p>
              <div class="flex flex-wrap gap-2 ml-4">
                <div
                  v-for="locationId in selectedLocationsPerSupplier[supplierId]"
                  :key="locationId"
                  class="flex items-center gap-2 bg-secondary-50 dark:bg-secondary-900/30 px-3 py-2 rounded-lg"
                >
                  <span class="text-sm text-neutral-700 dark:text-neutral-300">{{ getLocationName(supplierId, locationId) }}</span>
                  <button
                    @click="removeSelectedLocation(supplierId, locationId)"
                    class="w-6 h-6 flex items-center justify-center rounded-full bg-secondary-200 dark:bg-secondary-700 hover:bg-secondary-300 dark:hover:bg-secondary-600 text-secondary-700 dark:text-secondary-200 transition-colors"
                    aria-label="Remove location"
                  >
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Date Selection -->
    <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 p-6">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label for="rpt-start-date" class="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">
            Start Date
          </label>
          <input
            id="rpt-start-date"
            type="date"
            v-model="startDate"
            :max="endDate || undefined"
            class="w-full px-4 py-2.5 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 transition-all duration-200"
          />
        </div>
        <div>
          <label for="rpt-end-date" class="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">
            End Date
          </label>
          <input
            id="rpt-end-date"
            type="date"
            v-model="endDate"
            :min="startDate || undefined"
            class="w-full px-4 py-2.5 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 transition-all duration-200"
          />
        </div>
      </div>
      <p v-if="dateRangeInvalid" class="mt-2 text-xs text-red-600 dark:text-red-400">
        End date must be on or after the start date.
      </p>
    </div>

    <!-- Email Addresses -->
    <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 p-6 space-y-4">
      <div>
        <label for="rpt-emails" class="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">
          Email Addresses (comma separated)
        </label>
        <input
          id="rpt-emails"
          type="email"
          multiple
          autocomplete="email"
          v-model="emailList"
          @blur="onEmailBlur"
          placeholder="example1@example.com, example2@example.com"
          class="w-full px-4 py-2.5 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 transition-all duration-200"
        />
        <p v-if="emailError" class="mt-1.5 text-xs text-red-600 dark:text-red-400">{{ emailError }}</p>
      </div>
    </div>

    <!-- Send Report Button -->
    <div>
      <button
        @click="runReport"
        :disabled="!canRunReport || isSending"
        class="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary-700 text-white hover:bg-primary-800 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span class="material-icons text-[18px]" :class="{ 'animate-spin': isSending }" aria-hidden="true">{{ isSending ? 'autorenew' : 'send' }}</span>
        {{ isSending ? 'Sending...' : 'Send Report' }}
      </button>
    </div>

    <!-- Past Reports -->
    <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 overflow-hidden">
      <div class="px-6 py-4 border-b border-neutral-100 dark:border-neutral-700">
        <h2 class="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Past Reports</h2>
      </div>
      <div class="p-6">
      <ErrorState
        v-if="reportsError && !pastReports.length"
        title="Couldn't load past reports"
        :message="reportsError"
        @retry="fetchPastReports"
      />
      <template v-else>
      <AlertBanner v-if="reportsError" type="error" message="Couldn't refresh past reports — showing the last loaded list." :dismissible="false" class="mb-4" />
      <DataTable
        :data="pastReports"
        :columns="pastReportsColumns"
        :loading="isLoadingReports"
        :searchable="false"
        :show-index="false"
        :pagination="true"
        loading-text="Loading past reports..."
        empty-text="No past reports found"
      >
        <template #cell-type="{ row }">
          <span class="text-sm text-neutral-700 dark:text-neutral-300">{{ getReportTypeName(row.type) }}</span>
        </template>

        <template #cell-parameters="{ row }">
          <span class="text-sm text-neutral-700 dark:text-neutral-300">{{ getReportParameters(row) }}</span>
        </template>

        <template #cell-emailList="{ row }">
          <span class="text-sm text-neutral-700 dark:text-neutral-300">{{ row.emailList || "-" }}</span>
        </template>

        <template #cell-runAt="{ row }">
          <span class="text-sm text-neutral-700 dark:text-neutral-300">{{ formatTimestamp(row.runAt) }}</span>
        </template>

        <template #actions="{ row }">
          <div class="flex items-center gap-1 justify-center">
            <button
              @click="reRunReport(row)"
              class="p-1.5 rounded-lg text-neutral-500 dark:text-neutral-400 hover:text-secondary-600 hover:bg-secondary-50 dark:hover:text-secondary-400 dark:hover:bg-secondary-900/30 transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              title="Re-run report"
              aria-label="Re-run report"
            >
              <span class="material-icons text-[16px]" aria-hidden="true">replay</span>
            </button>
            <button
              @click="confirmDeleteReport(row)"
              class="p-1.5 rounded-lg text-neutral-500 dark:text-neutral-400 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-900/30 transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              title="Delete report"
              aria-label="Delete report"
            >
              <span class="material-icons text-[16px]" aria-hidden="true">delete_outline</span>
            </button>
          </div>
        </template>
      </DataTable>
      </template>
      </div>
    </div>

    <!-- Delete Past Report Confirm -->
    <ConfirmDialog
      :show="showDeleteReportConfirm"
      title="Delete Past Report"
      message="Delete this past report? This cannot be undone."
      confirm-label="Delete"
      :loading="isDeletingReport"
      danger
      @confirm="deleteReport"
      @cancel="showDeleteReportConfirm = false"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, reactive, watch, watchEffect } from "vue";
import { useStore } from "vuex";
import axios from "axios";
import {
  collection,
  addDoc,
  getDocs,
  Timestamp,
  query,
  where,
  orderBy,
  deleteDoc,
  doc,
  onSnapshot,
} from "firebase/firestore";
import { db } from "@/firebase/config";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import DataTable from "@/components/ui/DataTable.vue";
import ConfirmDialog from "@/components/ui/ConfirmDialog.vue";
import ErrorState from "@/components/ui/ErrorState.vue";
import AlertBanner from "@/components/ui/AlertBanner.vue";
import useToast from "@/composables/useToast";

export default {
  name: "ReportingView",
  components: {
    vSelect,
    DataTable,
    ConfirmDialog,
    ErrorState,
    AlertBanner,
  },
  setup() {
    const store = useStore();
    store.commit("setCurrentRoute", "Reports");
    const { showToast } = useToast();

    const selectedReportType = ref("supplier");

    // Start and end dates
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const startDate = ref(firstDayOfMonth.toISOString().substr(0, 10));
    const endDate = ref(today.toISOString().substr(0, 10));

    // Data variables
    const suppliers = ref([]);
    const suppliersWithLocations = ref([]);

    // Selected variables
    const selectedSupplierIds = ref([]);
    const selectedSupplierWithLocationsIds = ref([]);
    const selectedLocationsPerSupplier = reactive({});

    // Email
    const emailList = ref("");
    const emailError = ref(null);

    // Sending state
    const isSending = ref(false);

    // Past Reports
    const pastReports = ref([]);
    const isLoadingReports = ref(false);
    const reportsError = ref(null);
    const currentReRunReport = ref(null);
    const noDataAlert = ref(false);
    const showDeleteReportConfirm = ref(false);
    const reportToDelete = ref(null);
    const isDeletingReport = ref(false);

    // Define Past Reports table columns
    const pastReportsColumns = [
      { key: "type", label: "Report Type", sortable: true, align: "left" },
      { key: "parameters", label: "Parameters", sortable: false, align: "left" },
      { key: "emailList", label: "Email Addresses", sortable: false, align: "left" },
      { key: "runAt", label: "Date Run", sortable: true, align: "left" },
    ];

    // Fetch data on mounted
    onMounted(() => {
      fetchSuppliersFromFirestore();
      fetchPastReports();
    });

    // Load suppliers from Firestore instead of removed SQL endpoints
    const fetchSuppliersFromFirestore = () => {
      let colRef = collection(db, "suppliers");
      colRef = query(colRef, orderBy("suppliername"));

      const unsub = onSnapshot(colRef, (snapshot) => {
        const allSuppliers = [];
        const withLocations = [];

        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          if (data.siteid == store.state.currentSite) {
            const supplier = {
              supplierid: data.supplierid,
              suppliername: data.suppliername,
              locations: (data.locations || []).map((loc) => ({
                locationid: loc.locationid,
                locationname: loc.areaname || loc.locationname || `Location ${loc.locationid}`,
              })),
            };

            allSuppliers.push(supplier);

            if (supplier.locations.length > 0) {
              withLocations.push(supplier);
            }
          }
        });

        suppliers.value = allSuppliers;
        suppliersWithLocations.value = withLocations;
      });

      watchEffect((onInvalidate) => {
        onInvalidate(() => unsub());
      });
    };

    const dateRangeInvalid = computed(
      () => !!startDate.value && !!endDate.value && startDate.value > endDate.value
    );

    // Computed property to determine if the Send Report button should be enabled
    const canRunReport = computed(() => {
      if (!startDate.value || !endDate.value || !emailList.value.trim()) {
        return false;
      }
      if (dateRangeInvalid.value) {
        return false;
      }
      if (
        selectedReportType.value === "supplier" &&
        selectedSupplierIds.value.length > 0
      ) {
        return true;
      } else if (
        selectedReportType.value === "supplier_location" &&
        Object.keys(selectedLocationsPerSupplier).length > 0
      ) {
        return Object.values(selectedLocationsPerSupplier).some(
          (locations) => locations.length > 0
        );
      }
      return false;
    });

    // Methods to get names
    const getSupplierName = (supplierId) => {
      const supplier =
        suppliers.value.find((s) => s.supplierid === supplierId) ||
        suppliersWithLocations.value.find((s) => s.supplierid === supplierId);
      return supplier ? supplier.suppliername : "";
    };

    const getLocationsForSupplier = (supplierId) => {
      const supplier = suppliersWithLocations.value.find(
        (s) => s.supplierid === supplierId
      );
      return supplier ? supplier.locations : [];
    };

    const getLocationName = (supplierId, locationId) => {
      const supplier = suppliersWithLocations.value.find(
        (s) => s.supplierid === supplierId
      );
      if (supplier) {
        const location = supplier.locations.find(
          (loc) => loc.locationid === locationId
        );
        return location ? location.locationname : "";
      }
      return "";
    };

    // Methods to remove selections
    const removeSelectedSupplier = (supplierId) => {
      selectedSupplierIds.value = selectedSupplierIds.value.filter(
        (id) => id !== supplierId
      );
    };

    const removeSelectedLocation = (supplierId, locationId) => {
      if (selectedLocationsPerSupplier[supplierId]) {
        selectedLocationsPerSupplier[supplierId] = selectedLocationsPerSupplier[
          supplierId
        ].filter((id) => id !== locationId);
      }
    };

    // Watch for changes in selected suppliers to maintain selected locations
    watch(selectedSupplierWithLocationsIds, (newVal, oldVal) => {
      const removedSuppliers = oldVal.filter((id) => !newVal.includes(id));
      removedSuppliers.forEach((supplierId) => {
        delete selectedLocationsPerSupplier[supplierId];
      });
    });

    // Email validation: returns the parsed list, or null (setting emailError)
    const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const validateEmails = () => {
      const tokens = emailList.value
        .split(",")
        .map((email) => email.trim())
        .filter((email) => email.length);
      if (!tokens.length) {
        emailError.value = "Enter at least one email address.";
        return null;
      }
      const invalid = tokens.filter((email) => !EMAIL_RE.test(email));
      if (invalid.length) {
        emailError.value = `Invalid email address${invalid.length > 1 ? "es" : ""}: ${invalid.join(", ")}`;
        return null;
      }
      emailError.value = null;
      return tokens;
    };

    const onEmailBlur = () => {
      if (emailList.value.trim()) validateEmails();
      else emailError.value = null;
    };

    // Send email report via the unified API
    const runReport = async () => {
      if (isSending.value) return;
      noDataAlert.value = false;
      const emails = validateEmails();
      if (!emails) return;
      isSending.value = true;
      try {
        const reportTitle = "UCO Collection Report";
        let payload = {
          start_date: startDate.value,
          end_date: endDate.value,
          report_title: reportTitle,
        };

        if (selectedReportType.value === "supplier") {
          payload.supplier_ids = selectedSupplierIds.value;
          payload.group_name = "Selected Suppliers";
        } else if (selectedReportType.value === "supplier_location") {
          const supplierIds = selectedSupplierWithLocationsIds.value;
          const locationData = {};
          supplierIds.forEach((supplierId) => {
            locationData[supplierId] =
              selectedLocationsPerSupplier[supplierId] || [];
          });
          const allSupplierIds = [];
          const allLocationIds = [];
          for (const supplierId of supplierIds) {
            if (
              locationData[supplierId] &&
              locationData[supplierId].length > 0
            ) {
              allSupplierIds.push(supplierId);
              allLocationIds.push(...locationData[supplierId]);
            }
          }
          payload.supplier_ids = allSupplierIds;
          payload.location_ids = allLocationIds.map((id) => parseInt(id));
          payload.group_name = "Selected Suppliers and Locations";
        }

        let reportTypeForApi =
          selectedReportType.value === "supplier_location"
            ? "supplierwlocation"
            : selectedReportType.value;

        const emailPayload = {
          report_type: reportTypeForApi,
          email_addresses: emails,
          ...payload,
        };

        await axios.post(`${store.state.apiUrl}/email_report`, emailPayload);
        showToast("success", "Report sent");

        const parametersChanged =
          !currentReRunReport.value ||
          JSON.stringify(currentParameters()) !==
            JSON.stringify(currentReRunReport.value.parameters);

        if (parametersChanged) {
          await saveReportParameters();
          await fetchPastReports();
        }
        currentReRunReport.value = null;
      } catch (error) {
        if (error.response && error.response.status === 404) {
          noDataAlert.value = true;
        } else {
          console.error("Error sending report:", error);
          showToast("error", "Failed to send report. Please try again.");
        }
      } finally {
        isSending.value = false;
      }
    };

    const currentParameters = () => {
      const params = {
        start_date: startDate.value,
        end_date: endDate.value,
      };
      if (selectedReportType.value === "supplier") {
        params.supplier_ids = selectedSupplierIds.value;
        params.supplier_names = selectedSupplierIds.value.map((id) =>
          getSupplierName(id)
        );
      } else if (selectedReportType.value === "supplier_location") {
        params.supplier_ids = selectedSupplierWithLocationsIds.value;
        params.locations = {};
        selectedSupplierWithLocationsIds.value.forEach((supplierId) => {
          params.locations[supplierId] =
            selectedLocationsPerSupplier[supplierId] || [];
        });
      }
      return params;
    };

    // Save report parameters in Firestore
    const saveReportParameters = async () => {
      try {
        const reportData = {
          type: selectedReportType.value,
          parameters: currentParameters(),
          runAt: Timestamp.now(),
          siteId: store.state.currentSite,
          emailList: emailList.value,
        };
        await addDoc(collection(db, "PastReports"), reportData);
      } catch (error) {
        // The report itself was already sent — only the history entry failed.
        console.error("Error saving report parameters:", error);
        showToast("error", "Report sent, but it couldn't be saved to Past Reports.");
      }
    };

    const fetchPastReports = async () => {
      isLoadingReports.value = true;
      try {
        const q = query(
          collection(db, "PastReports"),
          where("siteId", "==", store.state.currentSite)
        );
        const querySnapshot = await getDocs(q);
        const results = [];
        querySnapshot.forEach((doc) => {
          results.push({
            id: doc.id,
            ...doc.data(),
          });
        });
        results.sort((a, b) => b.runAt.toMillis() - a.runAt.toMillis());
        pastReports.value = results;
        reportsError.value = null;
      } catch (error) {
        console.error("Error fetching past reports:", error);
        reportsError.value = "Failed to load past reports. Please try again.";
      } finally {
        isLoadingReports.value = false;
      }
    };

    const getReportParameters = (report) => {
      if (report.type === "group") {
        return `Group: ${report.parameters.group_name || "N/A"}`;
      } else if (report.type === "supplier") {
        const supplierNames = report.parameters.supplier_names || [];
        return `Suppliers: ${supplierNames.join(", ") || "N/A"}`;
      } else if (report.type === "supplier_location") {
        const supplierIds = report.parameters.supplier_ids || [];
        const locationNames = [];
        supplierIds.forEach((supplierId) => {
          const supplierName =
            getSupplierName(supplierId) || "Unknown Supplier";
          const locations = report.parameters.locations
            ? report.parameters.locations[supplierId]
            : [];
          if (locations && locations.length > 0) {
            locations.forEach((locationId) => {
              const locationName =
                getLocationName(supplierId, locationId) || "Unknown Location";
              locationNames.push(`${supplierName} - ${locationName}`);
            });
          } else {
            locationNames.push(`${supplierName} - No Locations`);
          }
        });
        return `Locations: ${locationNames.join(", ") || "N/A"}`;
      }
      return "";
    };

    const formatTimestamp = (timestamp) => {
      const date = timestamp.toDate();
      return date.toLocaleString();
    };

    const getReportTypeName = (type) => {
      if (type === "group") return "Group Report";
      if (type === "supplier") return "Supplier Report";
      if (type === "supplier_location") return "Supplier and Locations Report";
      return "";
    };

    const reRunReport = (report) => {
      selectedReportType.value = report.type === "group" ? "supplier" : report.type;
      startDate.value = report.parameters.start_date;
      endDate.value = report.parameters.end_date;
      emailList.value = report.emailList || "";
      if (report.type === "supplier") {
        selectedSupplierIds.value = report.parameters.supplier_ids;
      } else if (report.type === "supplier_location") {
        selectedSupplierWithLocationsIds.value = report.parameters.supplier_ids;
        report.parameters.supplier_ids.forEach((supplierId) => {
          selectedLocationsPerSupplier[supplierId] =
            report.parameters.locations[supplierId] || [];
        });
      }
      currentReRunReport.value = report;
      // The form lives at the top of a long page — make the result visible.
      window.scrollTo({ top: 0, behavior: "smooth" });
      showToast("info", "Parameters loaded from past report — review and press Send Report");
    };

    const confirmDeleteReport = (report) => {
      reportToDelete.value = report;
      showDeleteReportConfirm.value = true;
    };

    const deleteReport = async () => {
      if (!reportToDelete.value) return;
      isDeletingReport.value = true;
      try {
        await deleteDoc(doc(db, "PastReports", reportToDelete.value.id));
        pastReports.value = pastReports.value.filter(
          (r) => r.id !== reportToDelete.value.id
        );
        showDeleteReportConfirm.value = false;
        reportToDelete.value = null;
        showToast("success", "Report deleted");
      } catch (error) {
        console.error("Error deleting report:", error);
        showToast("error", "Failed to delete report. Please try again.");
      } finally {
        isDeletingReport.value = false;
      }
    };

    watch(
      [
        startDate,
        endDate,
        selectedSupplierIds,
        selectedSupplierWithLocationsIds,
        () => JSON.stringify(selectedLocationsPerSupplier),
      ],
      () => {
        noDataAlert.value = false;
      }
    );

    watch(emailList, () => {
      if (emailError.value) emailError.value = null;
    });

    return {
      selectedReportType,
      startDate,
      endDate,
      dateRangeInvalid,
      suppliers,
      suppliersWithLocations,
      selectedSupplierIds,
      selectedSupplierWithLocationsIds,
      selectedLocationsPerSupplier,
      emailList,
      getSupplierName,
      getLocationsForSupplier,
      getLocationName,
      removeSelectedSupplier,
      removeSelectedLocation,
      canRunReport,
      runReport,
      isSending,
      emailError,
      onEmailBlur,
      pastReports,
      pastReportsColumns,
      isLoadingReports,
      reportsError,
      fetchPastReports,
      getReportParameters,
      reRunReport,
      formatTimestamp,
      getReportTypeName,
      confirmDeleteReport,
      deleteReport,
      showDeleteReportConfirm,
      isDeletingReport,
      noDataAlert,
    };
  },
};
</script>

<style scoped>
@media (prefers-reduced-motion: no-preference) {
  .reports-page > * {
    animation: fadeSlideIn 0.35s cubic-bezier(0.4, 0, 0.2, 1) both;
  }
  .reports-page > *:nth-child(1) { animation-delay: 0s; }
  .reports-page > *:nth-child(2) { animation-delay: 0.04s; }
  .reports-page > *:nth-child(3) { animation-delay: 0.08s; }
  .reports-page > *:nth-child(4) { animation-delay: 0.12s; }
  .reports-page > *:nth-child(5) { animation-delay: 0.16s; }
  .reports-page > *:nth-child(6) { animation-delay: 0.2s; }
}

@keyframes fadeSlideIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>

<!-- vue-select theming lives in src/assets/styles/tailwind.css (.v-select-custom) -->

